import type { ReactNode } from 'react';
import type { FieldValues, UseFormReturn } from 'react-hook-form';
import { Loader2, type LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import type { MpApprovalDetail } from '../types/marketPromotion.types';
import { DonationDetailsCard } from './DonationDetailsCard';

interface DonationFormRejectAction {
  onClick: () => void;
  isPending: boolean;
}

interface DonationFormCardProps<TValues extends FieldValues> {
  donation: MpApprovalDetail;
  title: string;
  icon: LucideIcon;
  form: UseFormReturn<TValues>;
  onSubmit: (values: TValues) => void;
  onClose: () => void;
  submitLabel: string;
  isSubmitPending: boolean;
  rejectAction?: DonationFormRejectAction | undefined;
  paymentRows?: ReactNode;
  decisionRows: ReactNode;
  hasPictureFields?: boolean | undefined;
}

export function DonationFormCard<TValues extends FieldValues>({
  donation,
  title,
  icon,
  form,
  onSubmit,
  onClose,
  submitLabel,
  isSubmitPending,
  rejectAction,
  paymentRows,
  decisionRows,
  hasPictureFields = false,
}: DonationFormCardProps<TValues>) {
  const isBusy = isSubmitPending || rejectAction?.isPending === true;

  const actions = (
    <>
      <Button type="button" variant="outline" onClick={onClose}>
        Cancel
      </Button>
      {rejectAction === undefined ? null : (
        <Button
          type="button"
          variant="destructive"
          onClick={rejectAction.onClick}
          disabled={isBusy}
        >
          {rejectAction.isPending ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
          Reject
        </Button>
      )}
      <Button type="submit" disabled={isBusy}>
        {isSubmitPending ? (
          <>
            <Loader2 className="size-4 animate-spin" aria-hidden />
            Saving...
          </>
        ) : (
          submitLabel
        )}
      </Button>
    </>
  );

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <DonationDetailsCard
          donation={donation}
          header={{ title, icon, actions }}
          paymentRows={paymentRows}
          decisionRows={decisionRows}
          hasPictureFields={hasPictureFields}
        />
      </form>
    </Form>
  );
}
